import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { SurveyForm } from "./SurveyForm";
import { DONOR_DISMISS_KEY, fetchSurvey, submitSurvey } from "../../lib/surveys";

/**
 * Optional one-page pop-up after a gift lands. Opens ~1.5s after the
 * success state, remembers "No thanks" per checkout session, and never
 * asks twice for the same gift.
 */
export const DonorSurveyDialog = ({ sessionId, active }) => {
  const [survey, setSurvey] = useState(null);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!active || !sessionId) return;
    if (localStorage.getItem(`${DONOR_DISMISS_KEY}:${sessionId}`)) return;
    let cancelled = false;
    fetchSurvey("donor").then((s) => {
      if (cancelled || !s.enabled || !s.questions?.length) return;
      setSurvey(s);
      setTimeout(() => !cancelled && setOpen(true), 1500);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [active, sessionId]);

  const close = () => {
    localStorage.setItem(`${DONOR_DISMISS_KEY}:${sessionId}`, "1");
    setOpen(false);
  };

  const onSubmit = async (answers) => {
    const r = await submitSurvey("donor", { touchpoint: "post_gift", answers, ref: sessionId, contact_email: email.trim() || null });
    localStorage.setItem(`${DONOR_DISMISS_KEY}:${sessionId}`, "1");
    setDone(r.thanks);
  };

  if (!survey) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent data-testid="donor-survey-dialog" className="max-h-[90vh] overflow-y-auto border-border bg-bone p-0 sm:max-w-xl">
        <div className="border-b border-border bg-sand px-6 pt-7 pb-5 lg:px-8">
          <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">Optional, two minutes</p>
          <DialogTitle className="mt-2 font-serif text-3xl font-normal leading-tight text-ink">{survey.title}</DialogTitle>
          <DialogDescription className="mt-2 text-base leading-relaxed text-olive">{survey.intro}</DialogDescription>
        </div>
        <div className="px-6 py-6 lg:px-8">
          {done ? (
            <div data-testid="donor-survey-thanks">
              <p className="font-serif text-2xl text-ink">Thank you.</p>
              <p className="mt-2 text-base text-olive">{done}</p>
              <button type="button" onClick={() => setOpen(false)} data-testid="donor-survey-close" className="mt-6 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand hover:bg-forest">Close</button>
            </div>
          ) : (
            <>
              <SurveyForm
                survey={survey}
                onSubmit={onSubmit}
                extraFields={
                  <label className="block">
                    <span className="text-[13px] font-semibold uppercase tracking-[0.18em] text-olive">Email, only if you'd like a reply <span className="normal-case tracking-normal">(optional)</span></span>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="donor-survey-email" placeholder="you@example.com" className="mt-2 w-full rounded-md border border-border bg-sand px-3 py-2 text-base text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none" />
                  </label>
                }
              />
              <button type="button" onClick={close} data-testid="donor-survey-skip" className="mt-4 text-sm font-semibold text-olive underline-offset-4 hover:text-ink hover:underline">
                No thanks
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
